import React, { useState, useEffect, useRef } from "react";
import { MapContainer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { gpsAPI, logisticsAPI, routePlannerAPI } from "../utils/api";
import { Navigation, CheckCircle, XCircle, Loader, MapPin, Map as MapIcon } from "lucide-react";
import { MapLayersControl, LayerPreferenceSaver } from "../utils/mapUtils";

// Fix Leaflet marker icons for driver view
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

function numberedStopIcon(num) {
  return L.divIcon({
    className: "",
    html: `<div style="background:#E8956D;color:#fff;border:2px solid #fff;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${num}</div>`,
    iconSize: [26, 26], iconAnchor: [13, 13],
  });
}

function driverIcon() {
  return L.divIcon({
    className: "",
    html: `<div style="background:#637C64;color:#fff;border:3px solid #fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.5)"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg></div>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
  });
}

function FlyToPos({ pos }) {
  const map = useMap();
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 13), { animate: true }); }, [pos, map]);
  return null;
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";

function useClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  return t;
}

export default function DriverTracking() {
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(() => localStorage.getItem("driver_vehicle") || "");
  const [selectedRoute, setSelectedRoute] = useState(() => localStorage.getItem("driver_route") || "");
  const [tracking, setTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentPos, setCurrentPos] = useState(null);
  const [status, setStatus] = useState("stopped");
  const [updateCount, setUpdateCount] = useState(0);
  const [error, setError] = useState("");
  const [routeStops, setRouteStops] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const intervalRef = useRef(null);
  const clock = useClock();

  useEffect(() => {
    logisticsAPI.getVehicles().then(r => setVehicles(r.data || [])).catch(() => {});
    logisticsAPI.getRoutes().then(r => setRoutes((r.data || []).filter(r => !r.route_number?.startsWith("TEST")))).catch(() => {});
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // Load route stops for the map when a route is selected
  useEffect(() => {
    if (!selectedRoute) { setRouteStops([]); return; }
    const r = routes.find(rt => rt.id === selectedRoute);
    if (!r) return;
    routePlannerAPI.getDriverRoute(r.route_number)
      .then(res => setRouteStops((res.data?.stops || []).filter(s => s.lat && s.lng)))
      .catch(() => {});
  }, [selectedRoute, routes]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendLocation = async (vehicle, route) => {
    if (!navigator.geolocation) { setError("GPS not available"); return false; }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await gpsAPI.updateLocation({
              vehicle_number: vehicle,
              route_id: route || null,
              route_number: routes.find(r => r.id === route)?.route_number || "",
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              status: "en_route",
            });
            setCurrentPos({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: Math.round(pos.coords.accuracy) });
            setLastUpdate(new Date());
            setUpdateCount(c => c + 1);
            resolve(true);
          } catch { resolve(false); }
        },
        (err) => { setError(`GPS: ${err.message}`); resolve(false); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const startTracking = async () => {
    if (!selectedVehicle) { setError("Select your vehicle first"); return; }
    setError("");
    setStatus("starting");
    const ok = await sendLocation(selectedVehicle, selectedRoute);
    if (!ok) { setStatus("stopped"); return; }
    setTracking(true);
    setStatus("active");
    intervalRef.current = setInterval(() => sendLocation(selectedVehicle, selectedRoute), 30000);
    // Log departure checkpoint
    try {
      await gpsAPI.addCheckpoint({
        vehicle_number: selectedVehicle,
        route_id: selectedRoute || null,
        route_number: routes.find(r => r.id === selectedRoute)?.route_number || "",
        checkpoint_type: "departed_kitchen",
      });
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const stopTracking = async () => {
    setTracking(false);
    setStatus("stopped");
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    // Log return checkpoint
    try {
      await gpsAPI.addCheckpoint({
        vehicle_number: selectedVehicle,
        route_id: selectedRoute || null,
        route_number: routes.find(r => r.id === selectedRoute)?.route_number || "",
        checkpoint_type: "returned_kitchen",
      });
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const savePrefs = (vehicle, route) => {
    if (vehicle !== undefined) { setSelectedVehicle(vehicle); localStorage.setItem("driver_vehicle", vehicle); }
    if (route !== undefined) { setSelectedRoute(route); localStorage.setItem("driver_route", route); }
  };

  const mapsUrl = currentPos ? `https://maps.google.com/?q=${currentPos.lat},${currentPos.lng}` : null;

  return (
    <div className="fixed inset-0 bg-[#1a1a1a] flex flex-col text-white" data-testid="driver-tracking">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-[#111] border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Manna" className="w-9 h-9 rounded-lg object-contain" />
          <div>
            <p className="font-bold text-sm">Manna Trust</p>
            <p className="text-white/50 text-xs">Driver Location Sharing</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono text-lg tabular-nums">{clock.toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit"})}</p>
          <p className="text-white/40 text-[10px]">{clock.toLocaleDateString("en-IN", {day:"numeric",month:"short"})}</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-6">
        {/* Setup: Vehicle + Route selection */}
        {!tracking && (
          <div className="w-full max-w-sm space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-white/50 mb-2 block">Your Vehicle</label>
              <select value={selectedVehicle} onChange={e => savePrefs(e.target.value, undefined)}
                className="w-full bg-[#2a2a2a] border border-white/10 rounded-xl px-4 py-3.5 text-white text-base outline-none focus:border-[#E8956D]">
                <option value="">Select your vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-white/50 mb-2 block">Your Route (optional)</label>
              <select value={selectedRoute} onChange={e => savePrefs(undefined, e.target.value)}
                className="w-full bg-[#2a2a2a] border border-white/10 rounded-xl px-4 py-3.5 text-white text-base outline-none focus:border-[#E8956D]">
                <option value="">Select route</option>
                {routes.map(r => <option key={r.id} value={r.id}>Route {r.route_number} ({r.schools?.length || 0} schools)</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Tracking status display */}
        {tracking && (
          <div className="text-center space-y-4 w-full max-w-sm">
            <div className="relative mx-auto w-32 h-32">
              <div className="absolute inset-0 rounded-full bg-[#637C64]/20 animate-ping" style={{animationDuration:"2s"}} />
              <div className="absolute inset-2 rounded-full bg-[#637C64]/30 animate-ping" style={{animationDuration:"2s",animationDelay:"0.3s"}} />
              <div className="absolute inset-0 rounded-full bg-[#637C64] flex items-center justify-center">
                <Navigation size={40} className="text-white" />
              </div>
            </div>
            <div>
              <p className="text-xl font-bold text-[#637C64]">Location Sharing Active</p>
              <p className="text-white/60 text-sm mt-1">Updates every 30 seconds</p>
            </div>
            <div className="bg-[#2a2a2a] rounded-2xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/50">Vehicle</span><span className="font-bold">{selectedVehicle}</span></div>
              {selectedRoute && <div className="flex justify-between"><span className="text-white/50">Route</span><span className="font-bold">Route {routes.find(r=>r.id===selectedRoute)?.route_number}</span></div>}
              <div className="flex justify-between"><span className="text-white/50">Updates sent</span><span className="font-bold text-[#637C64]">{updateCount}</span></div>
              {lastUpdate && <div className="flex justify-between"><span className="text-white/50">Last update</span><span className="font-bold">{lastUpdate.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",second:"2-digit"})}</span></div>}
              {currentPos && (
                <div className="pt-2 border-t border-white/10">
                  <div className="flex justify-between"><span className="text-white/50">Accuracy</span><span className={`font-bold ${currentPos.acc < 20 ? "text-[#637C64]" : currentPos.acc < 50 ? "text-[#D98032]" : "text-[#C2624E]"}`}>±{currentPos.acc}m</span></div>
                  {mapsUrl && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 mt-2 text-blue-400 hover:underline text-xs"><MapPin size={11} />View on Google Maps</a>}
                  {routeStops.length > 0 && (
                    <button onClick={() => setShowMap(m => !m)}
                      className="mt-2 w-full flex items-center justify-center gap-1 text-xs bg-[#E8956D]/20 text-[#E8956D] rounded-lg py-1.5 font-bold">
                      <MapIcon size={11} /> {showMap ? "Hide" : "Show"} Route Map ({routeStops.length} stops)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Driver Route Map — shown when showMap is toggled */}
        {tracking && showMap && routeStops.length > 0 && (
          <div className="w-full max-w-sm rounded-2xl overflow-hidden border border-white/10" style={{ height: 320 }}>
            <MapContainer
              center={currentPos ? [currentPos.lat, currentPos.lng] : [routeStops[0].lat, routeStops[0].lng]}
              zoom={12}
              style={{ height: "100%", width: "100%" }}
            >
              <MapLayersControl defaultLayer="streets" />
              <LayerPreferenceSaver />
              {/* Route polyline */}
              {routeStops.length >= 2 && (
                <Polyline
                  positions={routeStops.map(s => [s.lat, s.lng])}
                  color="#E8956D" weight={3} opacity={0.8}
                />
              )}
              {/* Stop markers */}
              {routeStops.map(stop => (
                <Marker key={stop.school_id} position={[stop.lat, stop.lng]}
                  icon={numberedStopIcon(stop.sequence)}>
                  <Popup>
                    <div style={{ fontSize: 12 }}>
                      <strong>{stop.sequence}. {stop.school_name}</strong>
                      {stop.delivery_status !== "pending" && (
                        <div style={{ color: stop.delivery_status === "delivered" ? "green" : "orange" }}>
                          {stop.delivery_status}
                        </div>
                      )}
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ color: "#2563eb", fontSize: 11 }}>
                        Navigate →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Driver's live position */}
              {currentPos && (
                <>
                  <Marker position={[currentPos.lat, currentPos.lng]} icon={driverIcon()}>
                    <Popup><strong style={{ fontSize: 12 }}>You are here</strong></Popup>
                  </Marker>
                  <FlyToPos pos={[currentPos.lat, currentPos.lng]} />
                </>
              )}
            </MapContainer>
          </div>
        )}

        {/* Starting state */}
        {status === "starting" && (
          <div className="text-center">
            <Loader size={48} className="animate-spin text-[#E8956D] mx-auto mb-4" />
            <p className="text-white/70">Getting your location...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="w-full max-w-sm bg-[#C2624E]/20 border border-[#C2624E]/30 rounded-xl px-4 py-3 text-sm text-[#C2624E] flex items-center gap-2">
            <XCircle size={16} /> {error}
          </div>
        )}

        {/* Big action button */}
        {!tracking && status !== "starting" && (
          <button onClick={startTracking} disabled={!selectedVehicle}
            className="w-full max-w-sm bg-[#637C64] hover:bg-[#4e6350] text-white rounded-2xl py-6 font-bold text-xl flex items-center justify-center gap-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-xl"
            data-testid="start-gps-btn">
            <Navigation size={28} /> Start Sharing Location
          </button>
        )}

        {tracking && (
          <button onClick={stopTracking}
            className="w-full max-w-sm bg-[#C2624E] hover:bg-[#a85040] text-white rounded-2xl py-6 font-bold text-xl flex items-center justify-center gap-3 transition-colors shadow-xl"
            data-testid="stop-gps-btn">
            <XCircle size={28} /> Stop & Return to Kitchen
          </button>
        )}

        {/* Instructions */}
        {!tracking && status !== "starting" && (
          <div className="w-full max-w-sm text-center">
            <p className="text-white/30 text-xs leading-relaxed">
              Keep this page open while driving. Your location will be shared every 30 seconds with the Manna Trust operations team.
            </p>
          </div>
        )}
      </div>

      {/* Bottom link to full app */}
      <div className="px-5 pb-5 text-center flex-shrink-0">
        <a href="/vehicle-tracking" className="text-white/20 text-xs hover:text-white/40">← Back to Full Dashboard</a>
      </div>
    </div>
  );
}
